import { convertStringToArray } from '../utils';
import { defaultExcludeForNumber, defaultExcludeForString, getExtractDate } from '../../../components/query-builder/querybuilder.config';
import { IRulePayload } from '../types';
import { IData } from '../../../types';
const _ = require('lodash');

const excludeConditionOperator = [...defaultExcludeForString, 'is_duplicated'];
/** Base set of query-builder condition fields shared by conditions and actions. */
const conditionFields: IData[] = [
    { id: 'Event', name: 'Event', label: 'Event', input: 'text', type: 'string', excludes: defaultExcludeForString },
    { id: 'Venue', name: 'Venue', label: 'Venue', input: 'text', type: 'string', excludes: defaultExcludeForString },
    { id: 'EventUrl', name: 'EventUrl', label: 'EventUrl', input: 'text', type: 'string', excludes: defaultExcludeForString },
    {
        id: 'EventDate', name: 'EventDate', label: 'EventDate', input: 'text', type: 'date',
        placeholder: 'yyyy-MM-dd, Now.AddDays(1), Now.AddMonths(1), Now.AddYears(1)',
        validator: (r: any) => {
            if (Array.isArray(r.value)) {
                for (let i = 0; i < r.value.length; i++) {
                    if (!r.value[i] || !getExtractDate(r.value[i])) return false;
                }
                return true;
            } else return getExtractDate(r.value) !== null;
        }
    },
    { id: 'EventTime', name: 'EventTime', label: 'EventTime', input: 'text', type: 'date', inputType: 'time' },
    { id: 'Quantity', name: 'Quantity', label: 'Quantity', input: 'number', inputType: 'number', type: 'integer', excludes: defaultExcludeForNumber },
    // { id: 'SoldQuantity', name: 'SoldQuantity', label: 'SoldQuantity', input: 'number', inputType: 'number', type: 'integer', excludes: defaultExcludeForNumber },
    { id: 'Section', name: 'Section', label: 'Section', input: 'text', type: 'string', excludes: defaultExcludeForString },
    { id: 'Row', name: 'Row', label: 'Row', input: 'text', type: 'string', excludes: defaultExcludeForString },
    { id: 'Cost', name: 'Cost', label: 'Cost', input: 'text', type: 'string', excludes: ['between', 'not_between'] },
    { id: 'InHandDate', name: 'InHandDate', label: 'InHandDate', input: 'text', type: 'date', inputType: 'date' },
    { id: 'DeliveryMethod', name: 'DeliveryMethod', label: 'DeliveryMethod', input: 'text', type: 'string', excludes: defaultExcludeForString },
    { id: 'TicketID', name: 'TicketID', label: 'Ticket ID', input: 'text', type: 'string', excludes: defaultExcludeForString },
    { id: 'PriceLevel', name: 'PriceLevel', label: 'PriceLevel', input: 'text', type: 'string', excludes: defaultExcludeForString },
    { id: 'EventId', name: 'EventId', label: 'EventId', input: 'number', inputType: 'number', type: 'integer', excludes: defaultExcludeForNumber },
    { id: 'City', name: 'City', label: 'City', input: 'text', type: 'string', excludes: defaultExcludeForString },
    { id: 'State', name: 'State', label: 'State', input: 'text', type: 'string', excludes: defaultExcludeForString },
    {
        id: 'Notes', name: 'Notes', label: 'Notes', input: 'text', type: 'string',
        placeholder: 'Notes DO NOT HAVE comma (,)',
        excludes: defaultExcludeForString,
        validator: (r: any) => r.value && /^((?!,).)*$/.test(r.value)
    }
];

/** Default `react-querybuilder` empty-state data for a condition query. */
const conditionInitData = {
    combinator: 'AND',
    rules: [] as any[]
};
/** Default `react-querybuilder` empty-state data for an action query. */
const actionInitData = {
    combinator: 'modify',
    rules: [] as any[]
};

/** Single combinator used by the action query builder. */
const combinators = [
    { name: 'modify', label: '' }
];

/**
 * Builds the field list for the "action" (adjust) query builder: a copy of
 * {@link conditionFields} plus computation-only fields, each given an
 * `assign` operator and a placeholder describing its expression syntax.
 * @returns The action field definitions.
 */
function getActionFields (): IData[] {
    /**
     * Enhanced expression validation for numeric fields.
     *
     * Valid examples:
     *  '=x', '=x + 10', '=x*1.1', '=input.Field1', '=input.a123'
     *
     * Rejects:
     *  - Pure numbers after '=': =12, =12.5
     *  - Invalid field starting char: =input.123, =input._field
     *  - Syntax/Math errors: =x(, =x^, =x+, =x+*10, =x)
     *  - Multiple/Invalid x: =xxx, =x1, =1x, =X
     *  - Property access on x: =x.1, =x.abc
     *  - Wrong casing: =Input.field, =INPUT.field
     */
    function enhancedValidateExpression (r: any, acceptOnlyNumbers = false) {
        if (!r || typeof r.value !== 'string') return false;

        const value = r.value.trim();

        // 1. Accept simple numerical literals without '=' IF acceptOnlyNumbers flag is true (e.g., "12")
        if (acceptOnlyNumbers && /^\d+(\.\d+)?$/.test(value)) {
            return true;
        }

        // 2. Expressions must start with '='
        if (!value.startsWith('=')) return false;

        const expression = value.substring(1).trim();
        if (!expression) return false;

        // 3. MANDATORY CHECK: Must contain at least 'x' or 'input.'
        // Rejects pure numbers like =12, =100 + 50
        const hasX = /\bx\b/.test(expression);
        const hasInput = /input\./.test(expression);
        if (!hasX && !hasInput) {
            return false;
        }

        // 4. Strict casing check: 'input.' must be strictly lowercase
        const inputMatches = expression.match(/input\./gi);
        if (inputMatches && !inputMatches.every(m => m === 'input.')) {
            return false;
        }

        // 5. Extract valid 'input.FieldName' references (FieldName MUST start with a letter: [a-zA-Z])
        const fieldRefRegex = /input\.[a-zA-Z][a-zA-Z0-9_]*/g;
        const sanitizedExpr = expression.replace(fieldRefRegex, '1');

        // Reject if any invalid 'input' reference remains (e.g., =input.123, =input._abc, =input)
        if (/input/i.test(sanitizedExpr)) return false;

        // 6. Variable validation: Ensure all remaining alphanumeric tokens are strictly 'x' or pure numbers
        // Prevents: x1, 1x, xx, X, xaaa...
        const tokens = sanitizedExpr.match(/[a-zA-Z0-9_]+/g) || [];
        for (const token of tokens) {
            const isPureNumber = /^\d+(\.\d+)?$/.test(token);
            if (!isPureNumber && token !== 'x') {
                return false;
            }
        }

        // 7. Reject property access on x (e.g., =x.1, =x.abc)
        if (/x\./.test(sanitizedExpr)) return false;

        // 8. Whitelist allowed mathematical characters only (+ - * / . ( ) x numbers whitespace)
        if (!/^[x0-9\s+\-*\/\.()]+$/.test(sanitizedExpr)) return false;

        // 9. AST/Syntax Evaluation: Test if the expression forms a valid mathematical syntax
        const testMathExpr = sanitizedExpr.replace(/\bx\b/g, '1');

        try {
            new Function(`return (${testMathExpr});`)();
            return true;
        } catch (e) {
            // Rejects invalid syntax like =x(, =x^, =x+*, =x)
            return false;
        }
    }

    let arr: IData[] = _.cloneDeep(conditionFields);
    arr.push({
        id: 'UNITTAXEDCOST', name: 'UNITTAXEDCOST', label: 'UNITTAXEDCOST', input: 'text', type: 'string',
        placeholder: '=x + 10, =x*1.1, =input.Field1 where x is UNITTAXEDCOST',
        validator: (r: any) => enhancedValidateExpression(r),
    });
    arr.push({
        id: 'FaceValue', name: 'FaceValue', label: 'FaceValue', input: 'text', type: 'string',
        placeholder: '=x + 10, =x*1.1, =input.Field1 where x is FaceValue',
        validator: (r: any) => enhancedValidateExpression(r),
    });
    arr.push({
        id: 'FloorPrice', name: 'FloorPrice', label: 'FloorPrice', input: 'text', type: 'string',
        placeholder: '=x + 10, =x*1.1, =input.Field1 where x is FloorPrice',
        validator: (r: any) => enhancedValidateExpression(r),
    });
    arr.push({ id: 'IsShort', name: 'IsShort', label: 'IsShort', input: 'number', type: 'integer' });
    return arr.map((m: any) => {
        if (m.type === 'date' || m.type === 'integer') {
            m.type = 'string';
            delete m.inputType;
        }
        m.operators = [{ name: 'assign', label: 'assign' }];
        if (m.name === 'Event') m.placeholder = '=x.Replace("Event","NewEvent") where x is Event';
        else if (m.name === 'Venue') m.placeholder = '=x.Replace("Venue","NewVenue") where x is Venue';
        else if (m.name === 'Quantity') {
            m.placeholder = '=x + 10, =x*1.1, =input.Field1 where x is Quantity';
            m.validator = (r: any) => enhancedValidateExpression(r, true);
        }
        else if (m.name === 'InHandDate') m.placeholder = '=x.AddDays(1), =x.AddMonths(1), =x.AddYears(1) where x is InHandDate';
        else if (m.name === 'EventDate') m.placeholder = '=x.AddDays(1), =x.AddMonths(1), =x.AddYears(1) where x is EventDate';
        else if (m.name === 'Cost') {
            m.placeholder = '=x + 10, =x*1.1, =input.Field1 where x is Cost';
            m.validator = (r: any) => enhancedValidateExpression(r);
        }
        else if (m.name === 'Notes') m.placeholder = 'NO COMMA (,) in the notes or CSV LISTING break';
        else if (m.name === 'Section') m.placeholder = '=x.Replace("Section","NewSection") where x is Section';
        else if (m.name === 'Row') m.placeholder = '=x.Replace("Row","NewRow") where x is Row';
        return m;
    });
}

/**
 * Builds the field list for the "condition" (match) query builder: a copy
 * of {@link conditionFields} with an `Event Market` select field inserted
 * (populated from `context.marketTypes`) and a `TTS_Upstream` text field appended.
 * @param context - Object providing `marketTypes`, the list of exchange market type choices.
 * @returns The condition field definitions.
 */
function getConditionFields (context: any): IData[] {
    const { marketTypes } = context;
    let arr: IData[] = _.cloneDeep(conditionFields);
    arr.splice(3, 0, { id: 'Type', name: 'Type', label: 'Event Market', defaultValue: 'accesso', values: marketTypes.map((x: any) => ({ ...x, label: x.name })), input: 'select', type: 'string', valueEditorType: 'select', excludes: excludeConditionOperator });
    arr.push({ id: 'TTS_Upstream', name: 'TTS_Upstream', label: 'TTS Upstream', input: 'text', type: 'string', placeholder: 'contains .range for Row Spec only' });
    return arr;
}

/**
 * Builds a rule payload from a `[id, data]` filter-list tuple used to
 * pre-populate the "New Rule" dialog (e.g. from `csv-merge/index.tsx`'s
 * `onNewRule`). Returns `null` when `idAndData` isn't an in-progress
 * new-rule tuple (`id < 0` with `data` provided).
 * @param idAndData - Either a plain rule id, or a `[id, data]` tuple where `data` seeds the new rule's condition/fields.
 * @returns The seeded rule payload, or `null` when not applicable.
 */
function getRule (idAndData: any): IRulePayload | null {
    let id = idAndData;
    let data = undefined;

    if (idAndData instanceof Array) {
        id = idAndData[0];
        data = idAndData[1];
    }

    if (id < 0 && data !== undefined) {
        return {
            Condition: buildConditions(data),
            Action: data['action'] || { method: 'remove', rules: [] },
            Name: data['name'],
            Id: 0,
            CreatedBy: '',
            Timestamp: '',
            Tags: (data['tags'] as any)?.filter((x: any) => x),
            Priority: data['priority'],
            ExpiredDate: data['expiredDate'],
            Source: (convertStringToArray(data['sources']) || []).join(',')
        };
    }
    return null;

    function buildConditions (data: any): IData | null {
        let rules: IData[] = [];
        if (data['event'] !== undefined) rules.push({
            id: 'Event',
            field: 'Event',
            type: 'string',
            input: 'text',
            operator: 'contains',
            value: data['event']
        });
        if (data['venue'] !== undefined) rules.push({
            id: 'Venue',
            field: 'Venue',
            type: 'string',
            input: 'text',
            operator: 'contains',
            value: data['venue']
        });
        if (data['section'] !== undefined) rules.push({
            id: 'Section',
            field: 'Section',
            type: 'string',
            input: 'text',
            operator: 'equal',
            value: data['section']
        });
        if (data['row'] !== undefined) rules.push({
            id: 'Row',
            field: 'Row',
            type: 'string',
            input: 'text',
            operator: 'equal',
            value: data['row']
        });
        if (data['eventDate'] !== undefined) rules.push({
            id: 'EventDate',
            field: 'EventDate',
            type: 'date',
            input: 'text',
            operator: 'equal',
            value: data['eventDate']
        });
        if (data['eventTime'] !== undefined) {
            rules.push({
                id: 'EventTime',
                field: 'EventTime',
                type: 'date',
                input: 'text',
                operator: 'equal',
                value: data['eventTime']
            });
        }

        if (data['quantity'] !== undefined) rules.push({
            id: 'Quantity',
            field: 'Quantity',
            type: 'string',
            input: 'text',
            operator: 'greater_or_equal',
            value: data['quantity']
        });

        if (rules.length === 0) return null;

        return { 'condition': 'AND', 'rules': rules };
    }
}

export {
    combinators,
    actionInitData,
    conditionInitData,
    getActionFields,
    getConditionFields,
    getRule,
    getExtractDate
};